import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import api from "../services/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Loader2, Mail, Phone, MessageCircle, Instagram, Save, Eye } from "lucide-react";

export default function AdminContactSettings() {
  const [form, setForm] = useState(null);
  const [original, setOriginal] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/site-settings");
      setForm(data);
      setOriginal(JSON.parse(JSON.stringify(data)));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load settings");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const dirty = form && original && JSON.stringify(form) !== JSON.stringify(original);

  const update = (k, v) => setForm((prev) => ({ ...prev, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/admin/site-settings", form);
      setForm(data);
      setOriginal(JSON.parse(JSON.stringify(data)));
      toast.success("Contact settings saved. Live on the site now.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  if (loading || !form) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
      </div>
    );
  }

  // Preview the actual wa.me URL the button will use
  const waPreview = form.whatsapp_number
    ? `https://wa.me/${form.whatsapp_number.replace(/[^0-9]/g, "")}?text=${encodeURIComponent(form.whatsapp_default_message || "")}`
    : "(whatsapp_number empty)";

  return (
    <div className="space-y-6 p-4 md:p-6 max-w-3xl" data-testid="admin-contact-settings-page">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-slate-900 flex items-center gap-2">
            <MessageCircle className="h-6 w-6 text-emerald-500" />
            Contact Settings
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Admin-editable contact info used on the marketing site — footer, WhatsApp buttons, college landing page.
            Changes apply instantly on next page load — no redeploy needed.
          </p>
        </div>
        <Button
          onClick={save}
          disabled={!dirty || saving}
          className="bg-[#FF6B00] hover:bg-[#FF6B00]/90 text-white"
          data-testid="save-contact-settings-btn"
        >
          {saving ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Save className="h-4 w-4 mr-1.5" />}
          Save changes
        </Button>
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 p-5 md:p-6 space-y-5">
        {/* Email */}
        <div>
          <Label className="text-xs font-bold uppercase tracking-wider text-slate-700 flex items-center gap-1.5">
            <Mail className="h-3.5 w-3.5" /> Contact Email
          </Label>
          <Input
            value={form.contact_email || ""}
            onChange={(e) => update("contact_email", e.target.value)}
            placeholder="you@example.com"
            className="mt-1.5 h-10 rounded-lg"
            data-testid="contact-email-input"
          />
          <p className="text-[11px] text-slate-400 mt-1">Shown in footer. Click-to-email opens user's mail app.</p>
        </div>

        {/* Phone (tel:) */}
        <div>
          <Label className="text-xs font-bold uppercase tracking-wider text-slate-700 flex items-center gap-1.5">
            <Phone className="h-3.5 w-3.5" /> Contact Phone (display)
          </Label>
          <Input
            value={form.contact_phone || ""}
            onChange={(e) => update("contact_phone", e.target.value)}
            placeholder="+91 79958 65243"
            className="mt-1.5 h-10 rounded-lg"
            data-testid="contact-phone-input"
          />
          <p className="text-[11px] text-slate-400 mt-1">Shown in footer. Click opens phone dialer.</p>
        </div>

        {/* WhatsApp number (wa.me format) */}
        <div>
          <Label className="text-xs font-bold uppercase tracking-wider text-emerald-700 flex items-center gap-1.5">
            <MessageCircle className="h-3.5 w-3.5" /> WhatsApp Number <span className="text-[10px] font-normal text-emerald-600">(digits only, with country code)</span>
          </Label>
          <Input
            value={form.whatsapp_number || ""}
            onChange={(e) => update("whatsapp_number", e.target.value)}
            placeholder="917995865243"
            className="mt-1.5 h-10 rounded-lg font-mono"
            data-testid="whatsapp-number-input"
          />
          <p className="text-[11px] text-slate-500 mt-1">
            Format: country code + number, no spaces or dashes. India → <code className="bg-slate-100 px-1 rounded">91</code> + 10-digit.
            Example: <code className="bg-slate-100 px-1 rounded">917995865243</code>
          </p>
        </div>

        {/* Default WhatsApp message */}
        <div>
          <Label className="text-xs font-bold uppercase tracking-wider text-slate-700">
            Default WhatsApp Message
          </Label>
          <Textarea
            rows={3}
            value={form.whatsapp_default_message || ""}
            onChange={(e) => update("whatsapp_default_message", e.target.value)}
            placeholder="Hi PickNGo! I'd like to book a pickup."
            className="mt-1.5 rounded-lg"
            data-testid="whatsapp-default-msg-input"
          />
          <p className="text-[11px] text-slate-400 mt-1">
            Pre-filled in the customer's WhatsApp chat when they click "Book on WhatsApp".
          </p>
        </div>

        {/* Instagram */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <Label className="text-xs font-bold uppercase tracking-wider text-slate-700 flex items-center gap-1.5">
              <Instagram className="h-3.5 w-3.5" /> Instagram URL
            </Label>
            <Input
              value={form.instagram_url || ""}
              onChange={(e) => update("instagram_url", e.target.value)}
              placeholder="https://www.instagram.com/…"
              className="mt-1.5 h-10 rounded-lg"
              data-testid="instagram-url-input"
            />
          </div>
          <div>
            <Label className="text-xs font-bold uppercase tracking-wider text-slate-700">
              Instagram Handle (display)
            </Label>
            <Input
              value={form.instagram_handle || ""}
              onChange={(e) => update("instagram_handle", e.target.value)}
              placeholder="@pick_n_go2026"
              className="mt-1.5 h-10 rounded-lg"
              data-testid="instagram-handle-input"
            />
          </div>
        </div>
      </div>

      {/* Live WhatsApp link preview */}
      <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4" data-testid="whatsapp-preview">
        <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-emerald-700 mb-1.5">
          <Eye className="h-3.5 w-3.5" /> Live preview
        </div>
        <p className="text-[11px] text-emerald-800 mb-2">This is the link the "Book on WhatsApp" button will open:</p>
        <code className="block text-[11px] text-emerald-900 bg-white rounded px-2 py-1.5 font-mono break-all border border-emerald-100">
          {waPreview}
        </code>
        {form.whatsapp_number && (
          <button
            type="button"
            onClick={() => {
              try {
                const w = window.open(waPreview, "_blank", "noopener,noreferrer");
                if (!w) window.location.href = waPreview;
              } catch {
                window.location.href = waPreview;
              }
            }}
            className="inline-flex items-center gap-1.5 mt-2 text-xs font-semibold text-emerald-700 hover:text-emerald-900"
            data-testid="whatsapp-test-open-btn"
          >
            <MessageCircle className="h-3.5 w-3.5" /> Test open →
          </button>
        )}
        <p className="text-[10px] text-emerald-700 mt-2 italic leading-relaxed">
          💡 Testing inside this admin preview may hit browser sandbox limits and show
          <strong> "api.whatsapp.com blocked"</strong> — that's not a real bug. On your live domain
          <strong> www.pickngo.co.in</strong> and on any phone, the link opens WhatsApp normally.
          To verify properly, open <code className="bg-white px-1 rounded">{waPreview.substring(0, 45)}…</code> directly in a new browser tab or tap it on your phone.
        </p>
      </div>
    </div>
  );
}
